import type { LayoutConfig } from '../../engine/plant/types';

/**
 * P&ID for the crude unit: charge and preheat along the bottom left, the fired heater in
 * the middle, the tower and its products on the right, the overhead condenser on top.
 */
export const layout: LayoutConfig = {
  views: [
    {
      id: 'overview',
      name: 'Unit overview',
      viewBox: [0, 0, 1620, 920],
      nodes: [
        { equipment: 'SRC-CRUDE', x: 60, y: 720, label: 'Crude' },
        { equipment: 'P-101', x: 160, y: 720 },
        { equipment: 'FV-101', x: 255, y: 720 },
        { equipment: 'HX-101', x: 360, y: 720 },
        { equipment: 'HX-102', x: 500, y: 720 },
        { equipment: 'HX-103', x: 640, y: 720 },
        { equipment: 'SRC-FG', x: 790, y: 860, label: 'Fuel gas' },
        { equipment: 'FV-202', x: 790, y: 790 },
        { equipment: 'H-201', x: 790, y: 660 },
        { equipment: 'C-301', x: 1020, y: 440 },
        { equipment: 'SRC-CW', x: 1000, y: 90, label: 'Cooling water' },
        { equipment: 'FV-301', x: 1140, y: 90 },
        { equipment: 'SNK-CW', x: 1290, y: 90, label: 'CW return' },
        { equipment: 'D-302', x: 1270, y: 230 },
        { equipment: 'P-303', x: 1200, y: 372 },
        { equipment: 'FV-303', x: 1110, y: 372 },
        { equipment: 'FV-304', x: 1390, y: 230 },
        { equipment: 'SNK-NAP', x: 1500, y: 230, label: 'Naphtha' },
        { equipment: 'FV-305', x: 1160, y: 470 },
        { equipment: 'SNK-KER', x: 1290, y: 470, label: 'Kerosene' },
        { equipment: 'FV-306', x: 1160, y: 530 },
        { equipment: 'SNK-DSL', x: 1290, y: 530, label: 'Diesel' },
        { equipment: 'FV-307', x: 1160, y: 640 },
        { equipment: 'SNK-RES', x: 1290, y: 640, label: 'Residue' },
      ],
      pipes: [
        { id: 'p-suction', points: [86, 720, 142, 720], flowTag: 'S-101.Q', label: 'Crude' },
        { id: 'p-disch', points: [178, 720, 237, 720], flowTag: 'S-101.Q' },
        { id: 'p-hx1', points: [273, 720, 322, 720], flowTag: 'S-101.Q' },
        { id: 'p-hx2', points: [398, 720, 462, 720], flowTag: 'S-101.Q' },
        { id: 'p-hx3', points: [538, 720, 602, 720], flowTag: 'S-101.Q' },
        { id: 'p-heater', points: [678, 720, 730, 720, 730, 660, 756, 660], flowTag: 'S-101.Q', label: 'To the heater' },
        { id: 'p-fuel', points: [790, 840, 790, 814], flowTag: 'H-201.FUEL', utility: true },
        { id: 'p-fuel2', points: [790, 766, 790, 700], flowTag: 'H-201.FUEL', utility: true, label: 'Fuel gas' },
        { id: 'p-flash', points: [824, 660, 900, 660, 900, 500, 994, 500], flowTag: 'S-101.Q', label: 'Flash feed' },
        { id: 'p-ovhd', points: [1020, 330, 1020, 230, 1238, 230], flowTag: 'C-301.CONDENSATE', label: 'Overhead' },
        { id: 'p-cw', points: [1026, 90, 1122, 90], flowTag: 'S-CW.Q', utility: true, label: 'Cooling water' },
        { id: 'p-cw2', points: [1158, 90, 1264, 90], flowTag: 'S-CW.Q', utility: true },
        { id: 'p-cw3', points: [1090, 90, 1090, 230], flowTag: 'S-CW.Q', utility: true, label: 'Condenser' },
        { id: 'p-drum', points: [1270, 268, 1270, 372, 1218, 372], flowTag: 'S-303.Q' },
        { id: 'p-reflux', points: [1182, 372, 1128, 372], flowTag: 'S-303.Q' },
        { id: 'p-reflux2', points: [1092, 372, 1046, 372], flowTag: 'S-303.Q', label: 'Reflux' },
        { id: 'p-nap', points: [1302, 230, 1372, 230], flowTag: 'S-304.Q' },
        { id: 'p-nap2', points: [1408, 230, 1474, 230], flowTag: 'S-304.Q' },
        { id: 'p-ker', points: [1046, 470, 1142, 470], flowTag: 'CALC-305.OUT' },
        { id: 'p-ker2', points: [1178, 470, 1264, 470], flowTag: 'CALC-305.OUT' },
        { id: 'p-dsl', points: [1046, 530, 1142, 530], flowTag: 'CALC-306.OUT' },
        { id: 'p-dsl2', points: [1178, 530, 1264, 530], flowTag: 'CALC-306.OUT' },
        { id: 'p-res', points: [1020, 550, 1020, 640, 1142, 640], flowTag: 'CALC-307.OUT' },
        { id: 'p-res2', points: [1178, 640, 1264, 640], flowTag: 'CALC-307.OUT' },
      ],
      bubbles: [
        { ref: 'FIC-101', x: 255, y: 800, leaderTo: [255, 744], shared: true },
        { ref: 'FT-101', x: 196, y: 800, leaderTo: [240, 736] },
        { ref: 'TT-101', x: 700, y: 790, leaderTo: [692, 736] },
        { ref: 'TIC-201', x: 640, y: 600, leaderTo: [756, 650], shared: true },
        { ref: 'TT-201', x: 700, y: 600, leaderTo: [770, 630] },
        { ref: 'TT-202', x: 880, y: 600, leaderTo: [814, 634] },
        { ref: 'FIC-202', x: 880, y: 790, leaderTo: [808, 786], shared: true },
        { ref: 'FT-202', x: 880, y: 730, leaderTo: [806, 730] },
        { ref: 'AT-202', x: 700, y: 540, leaderTo: [770, 626] },
        { ref: 'PT-203', x: 700, y: 860, leaderTo: [764, 860] },
        { ref: 'PIC-301', x: 1140, y: 160, leaderTo: [1140, 114], shared: true },
        { ref: 'PT-301', x: 918, y: 262, leaderTo: [1000, 322] },
        { ref: 'PDT-301', x: 900, y: 400, leaderTo: [994, 420] },
        { ref: 'TIC-304', x: 918, y: 330, leaderTo: [994, 344], shared: true },
        { ref: 'TT-304', x: 1080, y: 296, leaderTo: [1042, 334] },
        { ref: 'LT-302', x: 1348, y: 300, leaderTo: [1302, 254] },
        { ref: 'LIC-302', x: 1390, y: 300, leaderTo: [1390, 254], shared: true },
        { ref: 'FIC-303', x: 1110, y: 440, leaderTo: [1110, 396], shared: true },
        { ref: 'FT-303', x: 1052, y: 440, leaderTo: [1080, 384] },
        { ref: 'FT-304', x: 1440, y: 296, leaderTo: [1440, 242] },
        { ref: 'FIC-305', x: 1160, y: 410, leaderTo: [1160, 446], shared: true },
        { ref: 'FT-305', x: 1218, y: 410, leaderTo: [1210, 458] },
        { ref: 'FIC-306', x: 1160, y: 590, leaderTo: [1160, 554], shared: true },
        { ref: 'FT-306', x: 1218, y: 590, leaderTo: [1210, 542] },
        { ref: 'LIC-307', x: 1082, y: 700, leaderTo: [1082, 646], shared: true },
        { ref: 'LT-307', x: 1020, y: 700, leaderTo: [1020, 590] },
        { ref: 'TT-305', x: 1100, y: 470, leaderTo: [1060, 470] },
        { ref: 'TT-306', x: 1100, y: 530, leaderTo: [1060, 530] },
        { ref: 'TT-307', x: 960, y: 620, leaderTo: [1006, 590] },
        { ref: 'TT-308', x: 1000, y: 160, leaderTo: [1000, 114] },
        { ref: 'FT-CW', x: 1230, y: 160, leaderTo: [1240, 104] },
        { ref: 'AT-304', x: 1500, y: 300, leaderTo: [1470, 244] },
        { ref: 'AT-306', x: 1290, y: 590, leaderTo: [1280, 546] },
      ],
      labels: [
        { x: 400, y: 630, text: 'CRUDE CHARGE AND PREHEAT TRAIN', size: 13, anchor: 'middle', muted: true },
        { x: 790, y: 566, text: 'FIRED HEATER', size: 13, anchor: 'middle', muted: true },
        { x: 1112, y: 178, text: 'OVERHEAD CONDENSER', size: 12, anchor: 'start', muted: true },
        { x: 1020, y: 592, text: 'ATMOSPHERIC COLUMN', size: 13, anchor: 'middle', muted: true },
      ],
      values: [
        { tag: 'TT-101.PV', x: 60, y: 810, label: 'Preheat outlet', units: 'K', decimals: 1, anchor: 'start' },
        { tag: 'HX-101.DUTY', x: 60, y: 832, label: 'Kerosene preheat', units: 'kW', decimals: 0, anchor: 'start' },
        { tag: 'HX-102.DUTY', x: 60, y: 854, label: 'Diesel preheat', units: 'kW', decimals: 0, anchor: 'start' },
        { tag: 'HX-103.DUTY', x: 60, y: 876, label: 'Residue preheat', units: 'kW', decimals: 0, anchor: 'start' },
        { tag: 'H-201.DUTY', x: 380, y: 810, label: 'Heater duty', units: 'MW', decimals: 2, anchor: 'start' },
        { tag: 'H-201.TSKIN', x: 380, y: 832, label: 'Tube skin', units: 'K', decimals: 1, anchor: 'start' },
        { tag: 'H-201.O2', x: 380, y: 854, label: 'Excess O₂', units: '%', decimals: 2, anchor: 'start' },
        { tag: 'H-201.FOUL', x: 380, y: 876, label: 'Coil fouling', units: '', decimals: 3, anchor: 'start' },
        { tag: 'C-301.SEP', x: 560, y: 190, label: 'Separation index', units: '', decimals: 3, anchor: 'start' },
        { tag: 'C-301.TOPPURITY', x: 560, y: 212, label: 'Overhead purity', units: '%', decimals: 2, anchor: 'start' },
        { tag: 'C-301.VLOAD', x: 560, y: 234, label: 'Vapour load', units: '× design', decimals: 3, anchor: 'start' },
        { tag: 'C-301.QCOND', x: 560, y: 256, label: 'Condenser duty', units: 'MW', decimals: 2, anchor: 'start' },
        { tag: 'AT-304.PV', x: 1360, y: 810, label: 'Naphtha endpoint', units: 'K', decimals: 1, anchor: 'start' },
        { tag: 'AT-306.PV', x: 1360, y: 832, label: 'Diesel 95 % point', units: 'K', decimals: 1, anchor: 'start' },
        { tag: 'CALC-QUAL.OUT', x: 1360, y: 854, label: 'Crude heaviness', units: '', decimals: 3, anchor: 'start' },
        { tag: 'PDT-301.PV', x: 1360, y: 876, label: 'Column ΔP', units: 'bar', decimals: 3, anchor: 'start' },
      ],
    },
  ],
};
